import clsx from 'clsx';
import { useState } from 'react';

/**
 * Re-usable `Badge` component. Can be made clickable.
 *
 * @param props
 * @param {React.ReactNode} [props.children]
 * @param {{
 *    placement?: 'start' | 'end';
 *    size?: number;
 *    color?: string;
 *    Component: React.ComponentType | undefined;
 * }} [props.icon]
 * @param {() => void} [props.onClick]
 * @param {boolean} [props.focused] - Control the `focused` state from the outside
 *
 * @param {string} [props.className]
 * @param {React.CSSProperties} [props.style]
 *
 * @returns {JSX.Element}
 */
export function Badge({ children, icon, onClick, focused, className, style }) {
  const [_focused, _setFocused] = useState(false);

  const clickable = !!onClick;
  const Component = clickable ? 'button' : 'div';

  const renderIcon = () => {
    if (!icon.Component) {
      return null;
    }

    return (
      <div className="inline-block shrink-0">
        <icon.Component size={icon.size ?? 14} color={icon.color} />
      </div>
    );
  };

  return (
    <Component
      type={clickable ? 'button' : undefined}
      className={clsx(
        'm-0',
        'inline-flex flex-row justify-center items-center gap-[6px]',
        'py-[6px] px-[12px]',
        'text-[16px] leading-none text-wtf-white',
        'outline-none',
        (focused ?? _focused) && 'bg-wtf-white/10',
        'transition-colors duration-300',
        className,
      )}
      style={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor:
          focused ?? _focused
            ? 'rgba(255, 255, 255, 0.4)'
            : 'rgba(255, 255, 255, 0.2)',
        borderRadius: '999px',
        ...style,
      }}
      onClick={onClick}
      onFocus={() => _setFocused(true)}
      onBlur={() => _setFocused(false)}
    >
      {icon?.placement === 'start' && renderIcon()}

      {children}

      {icon?.placement === 'end' && renderIcon()}
    </Component>
  );
}
